import React, { useEffect, useRef } from "react";
import Toggle from "./Toggle";

function parseIntervalSec() {
  const raw = process.env.REACT_APP_ADMIN_LIST_REFRESH_INTERVAL_SEC;
  const n = Number.parseInt(String(raw ?? "10"), 10);
  if (!Number.isFinite(n) || n < 1) return 10;
  return Math.min(n, 600);
}

export function readAutoRefreshEnabledFromStorage(key) {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeStored(key, enabled) {
  if (!key) return;
  try {
    localStorage.setItem(key, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/**
 * Toggle + interval refresh. Interval (seconds) from `REACT_APP_ADMIN_LIST_REFRESH_INTERVAL_SEC` (default 10).
 * When `storageKey` is set, enabled state is persisted in localStorage.
 *
 * @param {object} props
 * @param {boolean} props.enabled
 * @param {(next: boolean) => void} props.onEnabledChange
 * @param {() => void} props.onRefresh
 * @param {string} [props.storageKey] Optional localStorage key for enabled flag.
 */
const AutoRefreshToggle = ({ enabled, onEnabledChange, onRefresh, storageKey }) => {
  const sec = parseIntervalSec();
  const ivMs = sec * 1000;
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return undefined;
    const id = window.setInterval(() => {
      onRefreshRef.current?.();
    }, ivMs);
    return () => window.clearInterval(id);
  }, [enabled, ivMs]);

  const handleChange = (next) => {
    if (storageKey) writeStored(storageKey, next);
    onEnabledChange(next);
    if (next) onRefreshRef.current?.();
  };

  return (
    <Toggle
      label={`Auto-refresh (${sec}s)`}
      checked={enabled}
      onChange={handleChange}
    />
  );
};

export default AutoRefreshToggle;
